// npm packages
import React, { useEffect, useState, Fragment, useContext } from "react";
import * as moment from "moment";

// custom pages
import Context from "context/context";
import reportAPI from "apis/panel/reports/reports";
import "../Reports.css";
import { toast } from "react-toastify";
import { connectSocket } from "config/socket";
import Tables from "components/table/table";
import FileHandler from "apis/panel/fileHandler/fileHandler";
import DocumentsAPI from "apis/panel/Document/document";
import CertificatesAPI from "apis/panel/ConfigurationPage/configuration";

// material ui
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Autocomplete from "@mui/material/Autocomplete";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled } from "@mui/system";
import PropTypes from "prop-types";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

function CircularProgressWithLabel(props) {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress variant="determinate" {...props} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{ color: "text.secondary" }}
        >
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

CircularProgressWithLabel.propTypes = {
  /**
   * The value of the progress indicator for the determinate variant.
   * Value between 0 and 100.
   * @default 0
   */
  value: PropTypes.number.isRequired,
};

const CustomCheckbox = styled(Checkbox)({
  color: " #0056B2",
  "&.Mui-checked": {
    color: " #0056B2",
  },
});

const documentTypes = ["Resume", "Offer Letter", "Experience Letter"];

export default function EmployeeDocuments({ setDisableDropdown }) {
  const { state, dispatch } = useContext(Context);

  const {
    getAllEmployeesAPI,
    getAllDepartmentsAPI,
    getEmployeeDocumentsAPI,
    getBackgroundLogsAPI,
  } = reportAPI();
  const { fileDirectDownloadAPI } = FileHandler();
  const { getAllDocumentsAPI } = DocumentsAPI();
  const { getCertificatesAPI } = CertificatesAPI();

  const [employees, setEmployees] = useState([]);
  const [checkedList, setCheckedList] = useState(new Set());
  const [allDept, setAllDept] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [exportObj, setExportObj] = useState({
    employees: [],
    documentTypes: [],
    status: "All",
  });
  const [submitClicked, setSubmitClicked] = useState(false);
  const [logsData, setLogsData] = useState([]);
  const [logsOffset, setLogsOffset] = useState(1);
  const [logsCount, setLogsCount] = useState(0);
  const [logsResponse, setLogsResponse] = useState(false);
  const [jobProgress, setJobProgress] = useState({});
  const [documents, setDocuments] = useState([]);
  const [certificates, setCertificates] = useState([]);

  const handleGetAllEmployees = async () => {
    const res = await getAllEmployeesAPI();
    setEmployees(res?.data?.employees || []);
  };

  const getAllDepartments = async () => {
    const res = await getAllDepartmentsAPI();

    const departments = res?.data?.departments;
    setCheckedList(
      new Set(departments.map((department) => department.department_id))
    );
    setAllDept(departments);
    setSelectAll(true);
  };

  const handleGetAllDocumentsAPI = async () => {
    try {
      let res = await getAllDocumentsAPI();
      if (res?.data) {
        let data = [];
        res?.data?.map((d) => {
          data = [
            ...data,
            {
              name: d?.documentName,
              code: d?.documentCode,
            },
          ];
        });
        setDocuments([
          ...(data || []),
          { name: "Experience Letter", code: "EXPERIENCE_LETTER" },
        ]);
      }
    } catch (err) {}
  };

  const handleGetAllDegreesAPI = async () => {
    try {
      let res = await getCertificatesAPI();
      if (res?.data) {
        let data = [];
        res?.data?.map((d) => {
          data = [
            ...data,
            {
              name: d?.certificateName,
              code: d?.certificateCode,
            },
          ];
        });
        setCertificates(data);
      }
    } catch (err) {}
  };

  useEffect(() => {
    handleGetAllEmployees();
    getAllDepartments();
    handleGetAllDocumentsAPI();
    handleGetAllDegreesAPI();
  }, []);

  const handleChange = (deptId) => {
    setCheckedList((prevList) => {
      const newCheckedList = new Set(prevList);
      if (newCheckedList.has(deptId)) {
        newCheckedList.delete(deptId);
      } else {
        newCheckedList.add(deptId);
      }
      if (newCheckedList.size === allDept.length) {
        setSelectAll(true);
      } else {
        setSelectAll(false);
      }
      return newCheckedList;
    });
  };

  const handleExport = async () => {
    let body = {};

    if (exportObj?.documentTypes?.length == 0) {
      toast.error("Document Type field can’t be empty.", {
        toastId: "document-type-error",
      });
      return;
    }

    const selectedDeptArray = Array.from(checkedList);

    if (exportObj?.employees?.length === 0 && selectedDeptArray?.length === 0) {
      toast.error(
        "You must provide at least one: Employee Name or Department Name.",
        {
          toastId: "emp-dept-error",
        }
      );
      return;
    }

    dispatch({ type: "SET_LOADING", payload: true });

    body = {
      documentTypes: exportObj?.documentTypes?.map((d) => d.code),
      status: exportObj?.status,
    };

    if (exportObj?.employees && exportObj?.employees?.length > 0) {
      body = {
        ...body,
        employeeIds: exportObj?.employees.map((e) => e.employeeId),
      };
    }

    if (selectedDeptArray?.length > 0) {
      body = {
        ...body,
        deptIds: selectedDeptArray,
      };
    }

    setDisableDropdown(true);

    const res = await getEmployeeDocumentsAPI(body);

    toast.success(
      res?.message
        ? res?.message
        : "Network issue is detected, please try again later.",
      {
        toastId: "document-process-initiated",
      }
    );

    if (logsOffset === 1) {
      handleGetBackgroundLogs();
    } else {
      setLogsOffset(1);
    }
    setDisableDropdown(false);
    dispatch({ type: "SET_LOADING", payload: false });
  };

  const handleSelectAll = () => {
    if (checkedList.size === allDept.length) {
      setCheckedList(new Set());
      setSelectAll(false);
    } else {
      const newCheckedList = new Set(
        allDept.map((dept) => dept?.department_id)
      );
      setCheckedList(newCheckedList);
      setSelectAll(true);
    }
  };

  useEffect(() => {
    if (!state.socket) return;

    const onProgress = (data) => {
      setJobProgress((prev) => ({
        ...prev,
        [data.jobId]: {
          progress: data.progress,
          completed: data.completed ?? false,
          url: data?.url?.Key || null,
        },
      }));
    };

    state.socket.on("download-progress", onProgress);

    return () => {
      state.socket.off("download-progress", onProgress);
    };
  }, [state.socket]);

  const handleGetBackgroundLogs = async () => {
    try {
      let values;
      values = {
        offset: (logsOffset - 1) * 10,
        limit: 10,
        processName: "UPLOAD_QUALITY_DATA",
      };

      const res = await getBackgroundLogsAPI(values);
      setLogsResponse(true);
      setLogsData(res?.data?.logs || []);
      setLogsCount(res?.data?.count);
    } catch (err) {}
  };

  useEffect(() => {
    handleGetBackgroundLogs();
  }, [logsOffset]);

  const handleFileDownload = async (id) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      await fileDirectDownloadAPI(id);
      dispatch({ type: "SET_LOADING", payload: false });
    } catch (error) {
      dispatch({ type: "SET_LOADING", payload: false });
      console.error("Error downloading file:", error);
    }
  };

  const statusLabels = {
    All: "Active & InActive",
    Active: "Active",
    Inactive: "InActive",
  };

  return (
    <Fragment>
      <Grid container spacing={4} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <Box className="label">Employee Name</Box>
          <Autocomplete
            multiple
            options={employees || []}
            getOptionLabel={(option) => option?.employeeName}
            value={exportObj?.employees || []}
            onChange={(event, newValues) => {
              setExportObj({ ...exportObj, employees: newValues });
            }}
            renderInput={(params) => <TextField {...params} />}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={6}>
          <Box className="label">
            Document Type
            <span style={{ color: "red" }}>*</span>
          </Box>
          <Autocomplete
            multiple
            options={[...documents, ...certificates] || []}
            getOptionLabel={(option) => option?.name}
            value={exportObj?.documentTypes || []}
            onChange={(event, newValues) => {
              setExportObj({ ...exportObj, documentTypes: newValues });
            }}
            renderInput={(params) => <TextField {...params} />}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={6}>
          <Box className="label">
            {" "}
            Status
            <span style={{ color: "red" }}>*</span>
          </Box>
          <FormControl fullWidth>
            <Select
              labelId="status"
              id="status"
              name="status"
              displayEmpty
              value={exportObj?.status}
              onChange={(e) => {
                setExportObj({ ...exportObj, status: e.target.value });
              }}
              inputProps={{ "aria-label": "Status" }}
              renderValue={(selected) => {
                if (!selected) {
                  return <em>Select Status</em>;
                }
                return statusLabels[selected];
              }}
            >
              <MenuItem value={"All"}>Active & InActive</MenuItem>
              <MenuItem value={"Active"}>Active</MenuItem>
              <MenuItem value={"Inactive"}>InActive</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={12} md={12} lg={12}>
          <FormControlLabel
            control={
              <CustomCheckbox checked={selectAll} onChange={handleSelectAll} />
            }
            label={
              <>
                <span
                  style={{
                    fontFamily: "Inter",
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                >
                  Department Name
                </span>
              </>
            }
          />
          {submitClicked && checkedList.size == 0 && (
            <Box sx={{ marginTop: "10px", color: "red", fontSize: "13px" }}>
              <span>*</span> This Field is Required
            </Box>
          )}
        </Grid>

        {allDept &&
          allDept.length > 0 &&
          allDept.map((dept, index) => (
            <Grid
              key={dept?.department_id}
              sx={{ height: "60px" }}
              item
              xs={12}
              sm={4}
              md={4}
              lg={4}
            >
              <Box>
                <FormControlLabel
                  label={dept?.department_name}
                  control={
                    <CustomCheckbox
                      checked={checkedList.has(dept?.department_id)}
                      onChange={() => handleChange(dept?.department_id)}
                    />
                  }
                />
              </Box>
            </Grid>
          ))}
      </Grid>
      <Grid container>
        <Grid item xs={12} style={{ textAlign: "right", padding: "20px 0px" }}>
          {(exportObj?.employees?.length > 0 ||
            exportObj?.documentTypes?.length > 0) && (
            <Button
              className="reset-btn"
              component="label"
              variant="contained"
              tabIndex={-1}
              startIcon={
                <ReplayIcon style={{ fontSize: 30, verticalAlign: "middle" }} />
              }
              onClick={() => {
                setExportObj({
                  employees: [],
                  documentTypes: [],
                });
              }}
            >
              Reset
            </Button>
          )}
          <Button
            className="download-btn"
            startIcon={
              <DownloadForOfflineIcon
                style={{ fontSize: 30, verticalAlign: "middle" }}
              />
            }
            onClick={handleExport}
          >
            Export
          </Button>
        </Grid>
      </Grid>

      {logsResponse && (
        <div>
          <Tables
            title={
              <Box sx={{ pt: 2, pb: 2 }} className="VraShiftsTitle">
                Logs
              </Box>
            }
            searchBar={false}
            heading={[
              "S.No",
              // "Job Name",
              "Initiated By",
              "Status",
              "Created",
              "Progress",
            ]}
            data={logsData?.map((d, index) => [
              (logsOffset - 1) * 10 + index + 1,
              ,
              // d.jobName,
              d.businessEmail,
              d.status === "Success"
                ? d.status
                : jobProgress?.[d?.jobId]?.completed
                ? "Success"
                : d.status,
              <span style={{ whiteSpace: "nowrap" }}>
                {moment(d.createdOn).format("MMM D, YYYY hh:mm A")}
              </span>,
              // d.progress,
              [
                d?.filePath ? (
                  <span
                    onClick={() => {
                      handleFileDownload(d?.filePath);
                    }}
                    className="download"
                  >
                    <DownloadForOfflineIcon
                      style={{ fontSize: 30, verticalAlign: "middle" }}
                    />{" "}
                    &nbsp;
                    {"download"}
                  </span>
                ) : jobProgress?.[d?.jobId]?.completed ? (
                  <span
                    onClick={() => {
                      handleFileDownload(jobProgress?.[d?.jobId]?.url);
                    }}
                    className="download"
                  >
                    <DownloadForOfflineIcon
                      style={{ fontSize: 30, verticalAlign: "middle" }}
                    />
                    &nbsp;
                    {"download"}
                  </span>
                ) : (
                  <CircularProgressWithLabel
                    value={
                      jobProgress?.[d?.jobId]?.progress || d?.progress || 0
                    }
                  />
                ),
              ],
            ])}
            offset={logsOffset}
            setOffset={setLogsOffset}
            limit={10}
            count={logsCount}
            placeholder=""
          />
        </div>
      )}
    </Fragment>
  );
}
